import {h, Component} from 'preact'
import axios from 'axios'
import globals from '../../utils/globals'
import InputGroup from '../ui-components/InputGroup'
import ButtonControl from '../button-control'
import Logo from '../assets/logo'
import SideImage from '../assets/control-login-side-image'
import Icon from "../assets";

type Credentials = {
  login: string
  password: string
  remember: boolean
  [field: string]: any
}

interface Props {
  isStaffLoginEnabled: boolean
  defaultCdn: string
  customerName: string
}

interface State {
  credentials: Credentials,
  formErrors: {
    [item: string]: any
  },
  backendError: string | JSX.Element
}

const REQUIRED_FIELDS: Array<string> = ['login', 'password']
const FORGOT_PASSWORD_URL: string = `${globals('communityUrl')}/member/forgotPassword`
export const LOGIN_ENDPOINT: string = '/member/login'
const STAFF_LOGIN_COMMUNITY_URL: string = `https://sso.api.insided.com/auth/insidedgodmode?customer=godmode&return_url=${globals('communityUrl')}/ssoproxy/return`
const STAFF_LOGIN_CONTROL_URL: string = `https://sso.api.insided.com/auth/insidedgodmode?customer=godmode&return_url=${globals('controlUrl')}/ssoproxy/return`

const DEFAULT_BACKEND_ERROR_MESSAGE: JSX.Element = <div>Something went wrong, please try again later</div>
const INVALID_CREDENTIALS_BACKEND_ERROR_MESSAGE: JSX.Element = <div>The email address/username or password you entered is incorrect. If you need to, you can <a href={FORGOT_PASSWORD_URL} target='__blank'>reset your password</a>.</div>

const ERROR_MESSAGES: { [key: string] : any} = {
  login: 'Please enter your email / username',
  password: 'Please enter your password',
}

const InvalidCredentialsAlert = ({ error } : { error: string | JSX.Element}) =>
    <div class='invalid-credentials-alert'>
      <Icon icon='forbidden' color='#E94C5C'/>
      { error && error }
    </div>

export default class LoginControl extends Component<Props, State> {
  constructor () {
    super()
    this.state = {
      credentials: this.getDefaultCredentials(),
      formErrors: {},
      backendError: ''
    }

    this.onInputChange = this.onInputChange.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
  }

  getDefaultCredentials (): Credentials {
    return {
      login: '',
      password: '',
      remember: false,
    }
  }
  onInputChange (e: ChangeEvent): void {
    const { target } = e
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    const { credentials, formErrors } = this.state

    if(REQUIRED_FIELDS.includes(name)) {
      if (typeof value !== "boolean") {
        const inputIsEmpty = !value.trim()
        if(inputIsEmpty) {
          this.setState({
            credentials: {...credentials, ...{[name]: value}},
            formErrors: {...formErrors, ...{[name]: ERROR_MESSAGES[name]}},
          })
          return
        }
      }
    }

    delete formErrors![name]

    this.setState({
      credentials: {...credentials, ...{[name]: value}},
      formErrors: formErrors
    })
  }

  validateForm(): boolean {
    let errors = {}
    const {credentials} = this.state

    Object.keys(credentials).forEach(item => {
      if(REQUIRED_FIELDS.includes(item)) {
        const formField = credentials[item]
        const value = formField.trim()
        if (!value) Object.assign(errors, {[item]: ERROR_MESSAGES[item]})
      }
    })

    if (Object.keys(errors).length > 0) {
      this.setState({formErrors: errors})
      return false
    }

    return true
  }

  async onSubmit (e: Event): Promise<void> {
    e.preventDefault()
    if (this.validateForm()) {
      const {credentials} = this.state
      try {
        const response = await axios.post(LOGIN_ENDPOINT, credentials)
        const {redirect} = response.data
        window.location.replace(redirect)
        window.sessionStorage.setItem('UserLoggedInControl', String(true))
      } catch (e) {
        const {status} = e.response

        if(status === 422) {
          this.setState({ credentials: this.getDefaultCredentials(), backendError: INVALID_CREDENTIALS_BACKEND_ERROR_MESSAGE })
          return
        }

        this.setState({ credentials: this.getDefaultCredentials(), backendError: DEFAULT_BACKEND_ERROR_MESSAGE})

        throw e
      }
    }
  }

  render ({ isStaffLoginEnabled, defaultCdn, customerName }: Props, { credentials: { login, password, remember }, formErrors, backendError }: State) {
    return <div className='control-login'>
      <Logo width='164' height='48' customClass='control-login-logo' />
      <SideImage customClass='control-login-side-image' />
      <form class='control-login-wrapper' onSubmit={this.onSubmit}>
        <h2 className='control-login-title'>Log in to {customerName}</h2>
        { backendError && <InvalidCredentialsAlert error={backendError} />}
        <div class='control-login-box'>
          <div class='login-input-group'>
            <InputGroup name='login' title='Email / Username' type='text' value={login} onChangeCallback={this.onInputChange} error={formErrors['login']} />
            <InputGroup name='password' title='Password' type='password' value={password} onChangeCallback={this.onInputChange} error={formErrors['password']} />
          </div>
          <div class='login-meta'>
            <InputGroup name='remember' title='Remember me' type='checkbox' checked={remember} onChangeCheckbox={this.onInputChange} />
            <a href={FORGOT_PASSWORD_URL} target='__blank' class='login-meta-forgot-password'>Forgot password?</a>
          </div>
          <ButtonControl type='submit' class='login-button' variant='brand' title='Log in' fullWidth />
          { isStaffLoginEnabled && <div class='staff-login-buttons'>
            <ButtonControl onClick={() => window.location.replace(STAFF_LOGIN_CONTROL_URL)} class='login-button' variant='purple' title='Staff log in - control' />
            <ButtonControl onClick={() => window.location.replace(STAFF_LOGIN_COMMUNITY_URL)} class='login-button' variant='purple' title='Staff log in - community' />
          </div>}
        </div>
      </form>
    </div>
  }
}
